import { inject, Injectable, signal } from "@angular/core";
import { NotificationService } from "@kno2/common/services/notifications";
import { AuthService } from "@kno2/core/auth";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { SessionService } from "@kno2/core/session";
import { filter, firstValueFrom } from "rxjs";
import { io, Socket } from "socket.io-client";
import { ContactUploadLatest } from "../contacts";

const SOCKET_CONNECT = "connect";
const SOCKET_CONNECT_ERROR = "connect_error";
const SOCKET_CONTACTS_UPLOAD_FINISHED = "ContactsUploadHasFinished";
const SOCKET_AUTH_REQUEST = "authRequest";
const SOCKET_AUTH_RESULT = "authRequestResult";

export type ContactsUploadHasFinishedResult = {
    userId: string;
    contactUploadLatest: ContactUploadLatest;
};

export type SocketAuthResult = {
    success: boolean;
    error?: unknown;
};

@Injectable({
    providedIn: "root"
})
export class SocketService {
    private readonly sessionService = inject(SessionService);
    private readonly authService = inject(AuthService);
    private readonly notificationService = inject(NotificationService);

    private readonly appConfig = inject(APP_CONFIG_TOKEN);

    private client: Socket;
    public readonly contactsUploadHasFinished = signal({} as ContactUploadLatest);

    public init() {
        const { socketUrl } = this.appConfig;

        if (!socketUrl) {
            console.log(`${this.constructor.name}: No socket URL provided. Socket service will not be initialized.`);
            return;
        }

        this.client = io(socketUrl, { withCredentials: true, transports: ["websocket"] });

        this.client.on(SOCKET_CONNECT, () => this.handleConnected());
        this.client.on(SOCKET_CONNECT_ERROR, (err) => this.handleConnectError(err));
        this.client.on(SOCKET_AUTH_RESULT, (result: SocketAuthResult) => this.handleAuthResult(result));

        this.client.on(SOCKET_CONTACTS_UPLOAD_FINISHED, (result: ContactsUploadHasFinishedResult) => this.handleContactsUploadHasFinished(result));
    }

    public disconnect(): void {
        this.client?.disconnect();
    }

    private async handleConnected(): Promise<void> {
        await firstValueFrom(this.sessionService.profileData$.pipe(filter((profile) => !!profile && !!Object.entries(profile).length)));
        const { userId } = this.sessionService.getProfile();
        const authToken = this.authService.accessToken;

        this.client.emit(SOCKET_AUTH_REQUEST, {
            apiUrl: this.appConfig.baseApiUrl,
            authToken,
            userId
        });
    }

    private handleConnectError(err: Error): void {
        console.error(`${this.constructor.name}: The socket connection was refused.`, err);
        this.disconnect();
    }

    private handleAuthResult(result: SocketAuthResult) {
        if (!result.success) {
            this.disconnect();
        }
    }

    private handleContactsUploadHasFinished(result: ContactsUploadHasFinishedResult) {
        this.contactsUploadHasFinished.set(result.contactUploadLatest);

        const { lastUpload } = result.contactUploadLatest;
        const { status, errorMessage } = lastUpload;

        if (status === "Failure") {
            this.notificationService.error(errorMessage);
        } else if (status === "Processing") {
            this.notificationService.warning("The file could not be imported as an upload is currently in progress.");
        } else if (status === "Success") {
            this.notificationService.success("Contacts imported successfully.");
        }
    }
}
